import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useState, useEffect } from 'react';
import classes from './addRowModalRoomBpmByRunType.module.scss';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';

interface FormData {
  runTypeId: number;
  roomTypeId: number;
  bpm: number;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface AddRowModalRoomBpmByRunTypeProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialValues?: FormData | null;
  isEdit: boolean;
  roomTypeOptions: DropdownOption[];
  runTypeOptions: DropdownOption[];
}

const AddRowModalRoomBpmByRunType = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit,
  roomTypeOptions,
  runTypeOptions
}: AddRowModalRoomBpmByRunTypeProps) => {
  const [formData, setFormData] = useState<FormData>({
    roomTypeId: 0,
    runTypeId: 0,
    bpm: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;

  useEffect(() => {
    if (initialValues && visible) {
      setFormData({ ...initialValues });
      setErrors({});
    } else if (!isEdit && visible) {
      // Reset form for new entry
      setFormData({
        roomTypeId: 0,
        runTypeId: 0,
        bpm: 0
      });
      setErrors({});
    }
  }, [initialValues, visible, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.roomTypeId || formData.roomTypeId === 0) {
      newErrors.roomTypeId = 'Room Type is required';
    }
    if (!formData.runTypeId || formData.runTypeId === 0) {
      newErrors.runTypeId = 'Run Type is required';
    }

    // BPM validation
    if (formData.bpm === null || formData.bpm === undefined) {
      newErrors.bpm = 'BPM is required';
    } else if (formData.bpm < 0) {
      newErrors.bpm = 'BPM cannot be negative';
    } else if (formData.bpm > 1000) {
      newErrors.bpm = 'BPM cannot exceed 1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
        // Only close modal on success
        onHide();
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for Room BPM by Run Type!');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Dialog
      header={
        isEdit
          ? 'Edit Room BPM by Run Type Configuration'
          : 'Add New Room BPM by Run Type Configuration'
      }
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '500px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formGroup}>
          <label>Room Type</label>
          <Dropdown
            value={formData.roomTypeId}
            options={roomTypeOptions}
            onChange={e => handleInputChange('roomTypeId', e.value)}
            placeholder="Select Room Type"
            className={`${classes.dropdown} ${errors.roomTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.roomTypeId && <small className="p-error">{errors.roomTypeId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Run Type</label>
          <Dropdown
            value={formData.runTypeId}
            options={runTypeOptions}
            onChange={e => handleInputChange('runTypeId', e.value)}
            placeholder="Select Run Type"
            className={`${classes.dropdown} ${errors.runTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.runTypeId && <small className="p-error">{errors.runTypeId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>BPM</label>
          <InputText
            value={formData.bpm.toString()}
            onChange={e => handleInputChange('bpm', parseInt(e.target.value || '0', 10))}
            keyfilter="pint"
            placeholder="Enter BPM"
            className={`${classes.inputText} ${errors.bpm ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.bpm && <small className="p-error">{errors.bpm}</small>}
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />
        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalRoomBpmByRunType;
